/**
 * StrategyStatusPanel — readable card surfacing the current operational
 * strategy state from the safe read-only endpoints:
 *   /api/v1/forex/no-trade-monitor
 *   /api/v1/forex/health
 *   /api/v1/system/status
 *
 * Hard rules honored:
 *   - Read-only. No mutation. No broker controls.
 *   - Never displays raw JSON, never displays raw ISO timestamps in the
 *     main content (only in collapsible Diagnostics).
 *   - Renders a clean LIMITED / UNAVAILABLE state if a source is missing.
 *   - Never fabricates strategy status or trade data.
 */
import React, { useState } from 'react';
import {
  Activity,
  Ban,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Lock,
  Loader2,
  Shield,
  ShieldAlert,
  ShieldCheck,
} from 'lucide-react';
import {
  fmtRelative,
  fmtEtTime,
  titleize,
  severityToClass,
  severityToLabel,
  Severity,
} from './formatters';
import { useStrategyStatus } from './useStrategyStatus';

function statusToSeverity(noTradeStatus: string | undefined): Severity {
  switch ((noTradeStatus || '').toUpperCase()) {
    case 'HEALTHY':
    case 'OK':
      return 'ok';
    case 'WARNING':
      return 'monitoring';
    case 'CRITICAL':
      return 'review';
    case 'EMERGENCY':
      return 'halt';
    case 'PENDING_STRATEGY_REVIEW':
      return 'review';
    default:
      return 'unknown';
  }
}

export default function StrategyStatusPanel() {
  const { bundle, loading } = useStrategyStatus();
  const [diagOpen, setDiagOpen] = useState(false);

  if (loading) {
    return (
      <section
        data-testid="strategy-status-panel-loading"
        className="border border-[var(--card-border)] bg-[var(--card-bg)] rounded-2xl p-5"
      >
        <div className="flex items-center gap-2 text-[var(--muted-text)]">
          <Loader2 size={14} className="animate-spin" />
          <span className="text-[11px] uppercase tracking-widest font-black">
            Loading strategy status…
          </span>
        </div>
      </section>
    );
  }

  if (!bundle || bundle.dataQuality === 'UNAVAILABLE') {
    return (
      <section
        data-testid="strategy-status-panel-unavailable"
        className="border border-[var(--card-border)] bg-[var(--card-bg)] rounded-2xl p-5"
      >
        <h3 className="text-sm font-black uppercase italic text-[var(--app-text)] flex items-center gap-2">
          <Shield size={14} /> Strategy Status
        </h3>
        <div className="mt-3 text-xs text-[var(--muted-text)]">
          Strategy status is not available yet.{' '}
          <span
            className={`inline-flex items-center gap-1 ml-2 px-2 py-0.5 text-[9px] font-black uppercase tracking-widest rounded-md border ${severityToClass(
              'unknown',
            )}`}
          >
            Source: UNAVAILABLE
          </span>
        </div>
      </section>
    );
  }

  const noTrade = bundle.noTrade;
  const health = bundle.health;
  const system = bundle.system;

  const sev = statusToSeverity(noTrade?.status);
  const strategyName = noTrade?.strategy_version || 'Forex Strategy';
  const strategyState = titleize(noTrade?.strategy_status);
  const dq = bundle.dataQuality;
  const dqClass =
    dq === 'LIVE'
      ? severityToClass('ok')
      : dq === 'LIMITED'
      ? severityToClass('limited')
      : severityToClass('unknown');

  const liveBlocked =
    Boolean(health?.live_trading_blocked) ||
    system?.live_enabled === false ||
    system?.live_allowed === false;
  const killSwitch = Boolean(system?.kill_switch);
  const safetyLock = Boolean(system?.safety_lock);
  const paperReady = Boolean(health?.forex_paper_trading_ready);
  const oandaOk = Boolean(health?.oanda_practice_connected);

  return (
    <section
      data-testid="strategy-status-panel"
      className="border border-[var(--card-border)] bg-[var(--card-bg)] rounded-2xl p-5"
    >
      <header className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="text-sm font-black uppercase italic text-[var(--app-text)] flex items-center gap-2">
          <Shield size={14} /> Strategy Status
        </h3>
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className={`inline-flex items-center gap-1 px-2 py-1 text-[9px] font-black uppercase tracking-widest rounded-md border ${severityToClass(
              sev,
            )}`}
            data-testid="strategy-status-severity"
          >
            <Activity size={10} />
            {severityToLabel(sev)}
          </span>
          <span
            className={`inline-flex items-center gap-1 px-2 py-1 text-[9px] font-black uppercase tracking-widest rounded-md border ${dqClass}`}
            data-testid="strategy-status-source"
          >
            Source: {dq}
          </span>
        </div>
      </header>

      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        <div className="bg-[var(--input-bg)]/40 border border-[var(--card-border)] rounded-xl p-3">
          <div className="text-[9px] font-black uppercase tracking-widest text-[var(--muted-text)]">
            Strategy
          </div>
          <div className="text-xs font-black text-[var(--app-text)] italic">{strategyName}</div>
          <div className="text-[10px] text-[var(--muted-text)] mt-0.5">{strategyState}</div>
        </div>

        <div className="bg-[var(--input-bg)]/40 border border-[var(--card-border)] rounded-xl p-3">
          <div className="text-[9px] font-black uppercase tracking-widest text-[var(--muted-text)]">
            Execution Mode
          </div>
          <div className="text-xs font-black text-[var(--app-text)] italic">
            {titleize(system?.execution_mode) || 'Simulation'}
          </div>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            {liveBlocked ? (
              <span
                className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest rounded border ${severityToClass(
                  'ok',
                )}`}
              >
                <Lock size={9} /> Live Blocked
              </span>
            ) : (
              <span
                className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest rounded border ${severityToClass(
                  'limited',
                )}`}
              >
                <ShieldAlert size={9} /> Live Possible
              </span>
            )}
            {killSwitch && (
              <span
                className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest rounded border ${severityToClass(
                  'halt',
                )}`}
              >
                <Ban size={9} /> Kill Switch
              </span>
            )}
            {safetyLock && (
              <span
                className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest rounded border ${severityToClass(
                  'lock',
                )}`}
              >
                <Lock size={9} /> Safety Lock
              </span>
            )}
          </div>
        </div>

        <div className="bg-[var(--input-bg)]/40 border border-[var(--card-border)] rounded-xl p-3">
          <div className="text-[9px] font-black uppercase tracking-widest text-[var(--muted-text)]">
            Paper Trading Ready
          </div>
          <div className="text-xs font-black text-[var(--app-text)] italic flex items-center gap-2">
            {paperReady ? (
              <>
                <CheckCircle2 size={12} className="text-emerald-600" /> Ready
              </>
            ) : (
              <>
                <ShieldAlert size={12} className="text-amber-600" /> Not Ready
              </>
            )}
          </div>
          <div className="text-[10px] text-[var(--muted-text)] mt-0.5">
            OANDA practice: {oandaOk ? 'connected' : 'not connected'}
          </div>
        </div>

        <div className="bg-[var(--input-bg)]/40 border border-[var(--card-border)] rounded-xl p-3">
          <div className="text-[9px] font-black uppercase tracking-widest text-[var(--muted-text)]">
            Last Evaluation
          </div>
          <div className="text-xs font-black text-[var(--app-text)] italic">
            {fmtRelative(noTrade?.last_evaluated_at)}
          </div>
          <div className="text-[10px] text-[var(--muted-text)] mt-0.5">
            {noTrade?.last_evaluated_at ? fmtEtTime(noTrade.last_evaluated_at) : '—'}
          </div>
        </div>

        <div className="bg-[var(--input-bg)]/40 border border-[var(--card-border)] rounded-xl p-3">
          <div className="text-[9px] font-black uppercase tracking-widest text-[var(--muted-text)]">
            Last Signal
          </div>
          <div className="text-xs font-black text-[var(--app-text)] italic">
            {fmtRelative(noTrade?.last_signal_at)}
          </div>
          <div className="text-[10px] text-[var(--muted-text)] mt-0.5">
            {noTrade?.last_signal_at ? fmtEtTime(noTrade.last_signal_at) : '—'}
          </div>
        </div>

        <div className="bg-[var(--input-bg)]/40 border border-[var(--card-border)] rounded-xl p-3">
          <div className="text-[9px] font-black uppercase tracking-widest text-[var(--muted-text)]">
            Last Trade
          </div>
          <div className="text-xs font-black text-[var(--app-text)] italic">
            {fmtRelative(noTrade?.last_trade_at)}
          </div>
          <div className="text-[10px] text-[var(--muted-text)] mt-0.5">
            {noTrade?.last_trade_at ? fmtEtTime(noTrade.last_trade_at) : '—'}
          </div>
        </div>
      </div>

      {Array.isArray(noTrade?.pair_universe) && noTrade!.pair_universe!.length > 0 && (
        <div className="mt-3">
          <div className="text-[9px] font-black uppercase tracking-widest text-[var(--muted-text)] mb-1">
            Pair Universe
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            {noTrade!.pair_universe!.map((p) => (
              <span
                key={p}
                className="px-2 py-0.5 text-[10px] font-black uppercase tracking-widest rounded-md border border-[var(--card-border)] bg-[var(--input-bg)]/40 text-[var(--app-text)]"
              >
                {p}
              </span>
            ))}
          </div>
        </div>
      )}

      {Array.isArray(noTrade?.reason_codes) && noTrade!.reason_codes!.length > 0 && (
        <div className="mt-3">
          <div className="text-[9px] font-black uppercase tracking-widest text-[var(--muted-text)] mb-1">
            Reason Codes
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            {noTrade!.reason_codes!.slice(0, 8).map((r, i) => (
              <span
                key={i}
                className={`px-2 py-0.5 text-[10px] font-black uppercase tracking-widest rounded-md border ${severityToClass(
                  'monitoring',
                )}`}
              >
                {titleize(r)}
              </span>
            ))}
          </div>
        </div>
      )}

      {noTrade?.display_message && (
        <p className="mt-3 text-xs text-[var(--muted-text)] italic">
          {noTrade.display_message}
        </p>
      )}

      <div className="mt-3">
        <button
          type="button"
          onClick={() => setDiagOpen((v) => !v)}
          className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-[var(--muted-text)] hover:text-[var(--app-text)]"
          data-testid="strategy-status-diag-toggle"
        >
          {diagOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          Diagnostics
        </button>
        {diagOpen && (
          <div className="mt-2 p-3 rounded-lg border border-[var(--card-border)] bg-[var(--input-bg)]/30">
            <ul className="text-[10px] text-[var(--muted-text)] space-y-1">
              <li>
                <span className="font-black uppercase tracking-widest mr-2">Fetched:</span>
                {fmtEtTime(bundle.fetchedAt)}
              </li>
              <li>
                <span className="font-black uppercase tracking-widest mr-2">Source quality:</span>
                {dq}
              </li>
              {noTrade?.expected_profile && (
                <li>
                  <span className="font-black uppercase tracking-widest mr-2">Expected:</span>
                  {noTrade.expected_profile}
                </li>
              )}
              {noTrade?.active_profile && (
                <li>
                  <span className="font-black uppercase tracking-widest mr-2">Active:</span>
                  {noTrade.active_profile}
                </li>
              )}
              {health?.practice_url && (
                <li>
                  <span className="font-black uppercase tracking-widest mr-2">OANDA:</span>
                  {health.practice_url}
                </li>
              )}
              {system?.health_status && (
                <li>
                  <span className="font-black uppercase tracking-widest mr-2">System health:</span>
                  {system.health_status}
                </li>
              )}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
}
